import { loggerCore } from './core';
import { getCallerLocation } from './utils/location';

import type { LogLevel, LogEntry, LoggerPlugin } from './types';

type Context = Record<string, unknown>;

type TraceContext = { traceId?: string };
type TagsContext = { tags?: string[] };
type SourceContext = { source?: string; digest?: string };
type BrowserErrorContext = { url?: string; line?: number; column?: number };

type LogCtx<Ctx> = Partial<Ctx> &
  TraceContext &
  TagsContext &
  SourceContext &
  BrowserErrorContext &
  Record<string, unknown>;

interface BaseLogger<Ctx extends Context = Record<never, never>> {
  log: (level: LogLevel, message: string, context?: LogCtx<Ctx>) => void;
  debug: (message: string, context?: LogCtx<Ctx>) => void;
  info: (message: string, context?: LogCtx<Ctx>) => void;
  warn: (message: string, context?: LogCtx<Ctx>) => void;
  error: (error: unknown, context?: LogCtx<Ctx>) => void;
  breadcrumb: (event: string, context?: Partial<Ctx>) => void;
  useEnhancer: (enhancer: () => Context | Promise<Context>) => void;
  usePlugin: (plugin: LoggerPlugin) => void;
  createChildLogger: <ChildCtx extends Context>(
    context: ChildCtx,
  ) => BaseLogger<Ctx & ChildCtx>;
  group: (name: string) => BaseLogger<Ctx & { group: string }>;
}

function createLogger<Ctx extends Context = Record<never, never>>(
  baseContext: Ctx = {} as Ctx,
): BaseLogger<Ctx> {
  const applyEnhancers = async (): Promise<Context> => {
    const enhancers = await Promise.all(
      loggerCore.getEnhancers().map(fn => Promise.resolve(fn())),
    );
    return Object.assign({}, ...enhancers);
  };

  const emit = async (
    level: LogLevel,
    message: string,
    context: Record<string, unknown> = {},
  ): Promise<void> => {
    const caller = getCallerLocation(4);
    const enhancers = await applyEnhancers();

    const entry: LogEntry = {
      level,
      message,
      context: {
        ...enhancers,
        ...baseContext,
        ...context,
      },
      timestamp: new Date(),
      file: caller.file,
      line: caller.line,
      functionName: caller.functionName,
      tags: Array.isArray(context.tags) ? context.tags : undefined,
      traceId:
        typeof context.traceId === 'string'
          ? context.traceId
          : typeof enhancers.traceId === 'string'
            ? enhancers.traceId
            : undefined,
    };
    await loggerCore.logEntry(entry);
  };

  return {
    log: (level, msg, ctx) => {
      void emit(level, msg, ctx);
    },
    debug: (msg, ctx) => {
      void emit('debug', msg, ctx);
    },
    info: (msg, ctx) => {
      void emit('info', msg, ctx);
    },
    warn: (msg, ctx) => {
      void emit('warn', msg, ctx);
    },
    error: (err, ctx) => {
      const error = err instanceof Error ? err : new Error(String(err));
      void emit('error', error.message, { ...ctx, stack: error.stack });
    },
    breadcrumb: (msg, ctx) => {
      void emit('info', msg, { ...ctx, _type: 'breadcrumb' });
    },
    useEnhancer: loggerCore.useEnhancer,
    usePlugin: loggerCore.use,
    createChildLogger: childCtx =>
      createLogger({ ...baseContext, ...childCtx }),
    group: name => createLogger({ ...baseContext, group: name }),
  };
}

export const logger = createLogger();
